/**
 * Org Settings → Members. List members, change role, remove, leave.
 *
 * Owners + admins invite by email. When this installation cannot send
 * email, the invite form says so and hands back the invitation link to
 * send by hand, and every pending invitation offers its link again.
 *
 * Visibility rules:
 *   - Anyone in the org can see the member list (so users can find
 *     out who's an admin to ask for help).
 *   - Owners + admins can change roles + remove members.
 *   - Each user has a "Leave organization" button on their own row;
 *     hidden for the sole owner (Better Auth's leaveOrganization
 *     would 4xx, but the UI hides it preemptively).
 */
import { useEffect, useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { Check, Copy, Mail, X } from 'lucide-react'
import {
  Button,
  Input,
  Label,
  Select,
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '@/components/ui'
import type { OrgWithRole, MemberWithUser, OrgRole, PendingInvitation } from '@/lib/api'
import { orgsAPI } from '@/lib/api'
import { useIdentity } from '@/hooks/useIdentity'
import { useCanI } from '@/hooks/useCanI'
import { useUserTimezone } from '@/hooks/useUserTimezone'
import { useEmailStatus } from '@/hooks/usePublicSettings'
import { formatDate } from '@/lib/utils'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import { ALL_ROLES } from '@ottotester/permissions'

export function OrgMembers({ org }: { org: OrgWithRole }) {
  const { state, refreshIdentity } = useIdentity()
  const navigate = useNavigate()
  // Each surface in this tab maps to its own matrix action. They
  // currently share the same role list (owner+admin), so all three
  // booleans coincide today — but if the matrix later splits (e.g.
  // a "team-lead" role gets `members.invite` without
  // `members.remove`), each affordance picks up its own answer
  // without a code change here.
  const canInvite = useCanI('members.invite', org._id)
  const canChangeRole = useCanI('members.changeRole', org._id)
  const canRemove = useCanI('members.remove', org._id)
  const canListInvitations = useCanI('invitations.list', org._id)
  const myUserId = state.status === 'ready' ? state.identity.user.id : ''

  const [members, setMembers] = useState<MemberWithUser[] | null>(null)
  const [pending, setPending] = useState<PendingInvitation[]>([])
  const [loadError, setLoadError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [confirmRemove, setConfirmRemove] = useState<MemberWithUser | null>(null)
  const [confirmLeave, setConfirmLeave] = useState(false)
  // Pending role change awaiting confirmation. Discrete actions like
  // role transitions get a confirm dialog (matches the Remove flow on
  // this same screen) rather than auto-saving on Select onChange —
  // matching how the rest of the app confirms changes.
  const [confirmRoleChange, setConfirmRoleChange] = useState<{ member: MemberWithUser; newRole: OrgRole } | null>(null)
  const [showInvite, setShowInvite] = useState(false)
  // With email off, invites hand back a link to send by hand.
  const emailOff = useEmailStatus() === 'off'

  async function reload() {
    try {
      const [memberResult, pendingResult] = await Promise.all([
        orgsAPI.listMembers(org._id),
        canListInvitations
          ? orgsAPI.listInvitations(org._id).catch(() => [])
          : Promise.resolve([]),
      ])
      setMembers(memberResult.members ?? [])
      setPending(pendingResult.filter(p => p.status === 'pending'))
      setLoadError(null)
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load members')
    }
  }

  useEffect(() => {
    void reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org._id])

  async function handleRoleChange(member: MemberWithUser, newRole: OrgRole) {
    setActionError(null)
    setBusyId(member.id)
    try {
      await orgsAPI.changeMemberRole(org._id, member.id, newRole)
      await reload()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to change role')
    } finally {
      setBusyId(null)
    }
  }

  async function handleRemove(member: MemberWithUser) {
    setActionError(null)
    setBusyId(member.id)
    try {
      await orgsAPI.removeMember(org._id, member.id)
      setConfirmRemove(null)
      await reload()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to remove')
    } finally {
      setBusyId(null)
    }
  }

  async function handleLeave() {
    setActionError(null)
    setBusyId('self')
    try {
      await orgsAPI.leave(org._id)
      await refreshIdentity()
      // After leaving, the org is gone from the user's identity. Send
      // them somewhere they can be (workspace runner via /me, or
      // /onboarding if they had only this org).
      navigate('/', { replace: true })
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to leave')
      setBusyId(null)
    }
  }

  if (loadError) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-destructive">{loadError}</p>
        <Button variant="outline" onClick={reload}>Retry</Button>
      </div>
    )
  }
  if (members === null) {
    return <p className="text-sm text-muted-foreground">Loading…</p>
  }

  const ownerCount = members.filter(m => m.role === 'owner').length
  const myMember = members.find(m => m.userId === myUserId)
  const iAmSoleOwner = myMember?.role === 'owner' && ownerCount === 1

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {members.length} member{members.length === 1 ? '' : 's'}
          {pending.length > 0 && ` · ${pending.length} pending invite${pending.length === 1 ? '' : 's'}`}
        </p>
        {canInvite && !showInvite && (
          <Button variant="outline" onClick={() => setShowInvite(true)}>
            Invite member
          </Button>
        )}
      </div>

      {canInvite && showInvite && (
        <InviteMemberForm
          orgId={org._id}
          emailOff={emailOff}
          onCancel={() => setShowInvite(false)}
          onSent={async () => {
            setShowInvite(false)
            await reload()
          }}
        />
      )}

      {canListInvitations && pending.length > 0 && (
        <PendingInvitationsList
          orgId={org._id}
          invitations={pending}
          emailOff={emailOff}
          onChanged={reload}
        />
      )}

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>User</TableHead>
            <TableHead className="w-40">Role</TableHead>
            <TableHead className="w-40 text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {members.map(m => {
            const isMe = m.userId === myUserId
            const disableRoleChange = !canChangeRole || busyId === m.id ||
              // Don't let the sole owner demote themselves
              (m.role === 'owner' && ownerCount === 1)
            return (
              <TableRow key={m.id}>
                <TableCell>
                  <div className="font-medium">{m.user.name || m.user.email}</div>
                  <div className="text-xs text-muted-foreground">{m.user.email}</div>
                </TableCell>
                <TableCell>
                  {canChangeRole && !isMe ? (
                    <Select
                      value={m.role}
                      // Open the confirm dialog instead of saving
                      // immediately. Bail when the new value matches
                      // the current role (no-op selection) so the
                      // dialog doesn't fire on a stray click.
                      onChange={e => {
                        const newRole = e.target.value as OrgRole
                        if (newRole === m.role) return
                        setConfirmRoleChange({ member: m, newRole })
                      }}
                      disabled={disableRoleChange}
                      options={ALL_ROLES.map(r => ({ value: r, label: r }))}
                    />
                  ) : (
                    <span className="text-sm">{m.role}</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  {isMe && !iAmSoleOwner && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setConfirmLeave(true)}
                      disabled={busyId !== null}
                    >
                      Leave
                    </Button>
                  )}
                  {canRemove && !isMe && m.role !== 'owner' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setConfirmRemove(m)}
                      disabled={busyId !== null}
                    >
                      Remove
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>

      {iAmSoleOwner && (
        <p className="text-xs text-muted-foreground">
          You're the sole owner. To leave the organization, transfer ownership
          first (Danger tab) or invite another owner.
        </p>
      )}

      {actionError && (
        <p className="text-sm text-destructive" role="alert">{actionError}</p>
      )}

      {confirmRemove && (
        <ConfirmDialog
          open
          onClose={() => setConfirmRemove(null)}
          title="Remove member"
          description={`Remove ${confirmRemove.user.name || confirmRemove.user.email} from ${org.name}? They'll lose access immediately.`}
          confirmLabel="Remove"
          confirmVariant="destructive"
          onConfirm={() => handleRemove(confirmRemove)}
          busy={busyId === confirmRemove.id}
        />
      )}

      {confirmRoleChange && (
        <ConfirmDialog
          open
          onClose={() => setConfirmRoleChange(null)}
          title="Change role"
          description={
            <>
              Change <strong>{confirmRoleChange.member.user.name || confirmRoleChange.member.user.email}</strong>'s
              role from <strong>{confirmRoleChange.member.role}</strong> to{' '}
              <strong>{confirmRoleChange.newRole}</strong> in {org.name}?
            </>
          }
          confirmLabel="Change role"
          onConfirm={async () => {
            const pending = confirmRoleChange
            await handleRoleChange(pending.member, pending.newRole)
            setConfirmRoleChange(null)
          }}
          busy={busyId === confirmRoleChange.member.id}
        />
      )}

      {confirmLeave && (
        <ConfirmDialog
          open
          onClose={() => setConfirmLeave(false)}
          title="Leave organization"
          description={`Leave ${org.name}? You'll lose access to its workspaces immediately. An admin can re-invite you later.`}
          confirmLabel="Leave"
          confirmVariant="destructive"
          onConfirm={handleLeave}
          busy={busyId === 'self'}
        />
      )}
    </div>
  )
}

// #526 PR 3 — org-tier invites now grant governance roles only.
// Operational access (tester / reviewer) is granted per-workspace
// via the workspace-members UI after acceptance, not via invitation.
const INVITABLE_ROLES: OrgRole[] = ['admin', 'billing', 'member']

/** The link an invited person opens to accept. Same path the
 * invitation email links to. */
function invitationLink(invitationId: string): string {
  return `${window.location.origin}/accept-invitation/${invitationId}`
}

/** "Copy link", then "Copied" for two seconds. When the browser refuses
 * the clipboard, or has none (a plain-http address that isn't
 * localhost), the link appears as selectable text beside the button, so
 * it can always be copied by hand. */
function CopyLinkButton({ link, label = 'Copy link' }: { link: string; label?: string }) {
  const [copied, setCopied] = useState(false)
  const [failed, setFailed] = useState(false)

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(link)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      setFailed(true)
    }
  }

  const Icon = copied ? Check : Copy
  return (
    <>
      {failed && (
        <Input
          readOnly
          value={link}
          aria-label={`Link to copy by hand: ${label}`}
          className="min-w-0 w-64 font-mono text-xs"
          autoFocus
          onFocus={(e) => e.currentTarget.select()}
        />
      )}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => void handleCopy()}
        aria-label={copied ? 'Copied' : label}
      >
        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
        {copied ? 'Copied' : 'Copy link'}
      </Button>
    </>
  )
}

function InviteMemberForm({
  orgId,
  emailOff,
  onCancel,
  onSent,
}: {
  orgId: string
  /** This installation can't send email, so the form makes a link to
   * send by hand instead of promising an email. */
  emailOff: boolean
  onCancel: () => void
  onSent: () => Promise<void>
}) {
  const tz = useUserTimezone()
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<OrgRole>('member')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [created, setCreated] = useState<PendingInvitation | null>(null)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    const trimmed = email.trim()
    if (!trimmed.includes('@')) {
      setError('Enter a valid email address.')
      return
    }
    setSubmitting(true)
    try {
      const invitation = await orgsAPI.createInvitation(orgId, { email: trimmed, role })
      if (emailOff) {
        // Show the link in place of the form; the list reloads on Done.
        setCreated(invitation)
        setSubmitting(false)
        return
      }
      await onSent()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send invitation')
      setSubmitting(false)
    }
  }

  function inviteAnother() {
    setCreated(null)
    setEmail('')
    setRole('member')
    setError(null)
  }

  if (created) {
    const link = invitationLink(created.id)
    return (
      <div className="rounded-md border p-4 space-y-3 bg-muted/30">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold flex items-center gap-2 min-w-0">
            <Check className="h-4 w-4 shrink-0 text-emerald-600" aria-hidden="true" />
            <span className="truncate">Invitation created for {created.email}</span>
          </h3>
          <button
            type="button"
            onClick={() => void onSent()}
            className="p-1 text-muted-foreground hover:text-foreground"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="text-sm text-muted-foreground">
          No email was sent. Send them this link in chat or any other way. It works only for{' '}
          {created.email} and expires on {formatDate(created.expiresAt, tz)}.
        </p>
        <div className="flex items-center gap-2">
          <Input
            readOnly
            value={link}
            aria-label="Invitation link"
            className="flex-1 min-w-0 font-mono text-xs"
            onFocus={(e) => e.currentTarget.select()}
          />
          <CopyLinkButton link={link} />
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={inviteAnother}>
            Invite someone else
          </Button>
          <Button type="button" onClick={() => void onSent()}>
            Done
          </Button>
        </div>
      </div>
    )
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-md border p-4 space-y-3 bg-muted/30"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <Mail className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          {emailOff ? 'Invite a teammate' : 'Invite by email'}
        </h3>
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="p-1 text-muted-foreground hover:text-foreground"
          aria-label="Cancel"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      {emailOff && (
        <p className="text-sm text-muted-foreground">
          This installation can't send email. You'll get a link to send them yourself.
        </p>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <div className="sm:col-span-2 space-y-1">
          <Label htmlFor="invite-email">Email</Label>
          <Input
            id="invite-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="teammate@example.com"
            disabled={submitting}
            autoFocus
            required
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="invite-role">Role</Label>
          <Select
            id="invite-role"
            value={role}
            onChange={(e) => setRole(e.target.value as OrgRole)}
            disabled={submitting}
            options={INVITABLE_ROLES.map(r => ({ value: r, label: r }))}
          />
        </div>
      </div>
      {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button type="submit" disabled={submitting || !email.trim()}>
          {emailOff
            ? (submitting ? 'Creating…' : 'Create invite link')
            : (submitting ? 'Sending…' : 'Send invitation')}
        </Button>
      </div>
    </form>
  )
}

function PendingInvitationsList({
  orgId,
  invitations,
  emailOff,
  onChanged,
}: {
  orgId: string
  invitations: PendingInvitation[]
  /** Offer each invitation's link again, so a lost one can be resent. */
  emailOff: boolean
  onChanged: () => Promise<void>
}) {
  const tz = useUserTimezone()
  const [cancelingId, setCancelingId] = useState<string | null>(null)

  async function handleCancel(id: string) {
    setCancelingId(id)
    try {
      await orgsAPI.cancelInvitation(orgId, id)
      await onChanged()
    } finally {
      setCancelingId(null)
    }
  }

  return (
    <div className="rounded-md border p-3 space-y-2 bg-muted/20">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Pending invitations
      </h3>
      <ul className="divide-y divide-border">
        {invitations.map(inv => (
          <li key={inv.id} className="flex items-center justify-between py-2 gap-3">
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{inv.email}</div>
              <div className="text-xs text-muted-foreground">
                {inv.role} · expires {formatDate(inv.expiresAt, tz)}
              </div>
            </div>
            <div className="flex flex-wrap justify-end gap-1.5">
              {emailOff && (
                <CopyLinkButton link={invitationLink(inv.id)} label={`Copy link for ${inv.email}`} />
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleCancel(inv.id)}
                disabled={cancelingId !== null}
              >
                {cancelingId === inv.id ? 'Canceling…' : 'Cancel'}
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}
